import type {ReactElement} from 'react';
import {useEffect, useRef} from 'react';
import './hex-picker.scss';
import {HexColorPicker, RgbaColorPicker} from '@postermywall/react-colorful';
import type {RGBA, Rgbacolor} from '@Utils/color.util';
import {rgbaToHex} from '@Utils/color.util';
import {hexToRgba} from '@Utils/color.util';
import {rgbacolorToRgba, rgbaToRgbacolor} from '@Utils/color.util';

interface HexPickerProps {
  className?: string;
  color: RGBA;
  isAlphaDisabled?: boolean;

  onChange(color: RGBA): void;

  onChangeEnded(color: RGBA): void;

  onPointerDown?(): void;
}

export function HexPicker({className = '', isAlphaDisabled = false, ...props}: HexPickerProps): ReactElement {
  const colorRef = useRef(props.color);
  const onPointerReleaseRef = useRef<(() => void) | null>(null);
  const color = isAlphaDisabled ? rgbaToHex(props.color, false) : props.color;

  const removePointerReleaseListeners = (): void => {
    if (onPointerReleaseRef.current) {
      window.removeEventListener('pointerup', onPointerReleaseRef.current);
      window.removeEventListener('pointercancel', onPointerReleaseRef.current);
      onPointerReleaseRef.current = null;
    }
  };

  // The release listeners must be window-level, not the picker's own onPointerUp/onPointerCancel
  // props: a mouse has no implicit pointer capture, so a drag released outside the picker fires
  // pointerup on whatever is under the cursor and the picker props never see it (covered by a
  // browser test). They are attached synchronously on pointer-down (a state+effect round-trip
  // can miss a quick tap's pointerup), and pointercancel is treated as a release too — a bottom
  // sheet stealing the gesture must still commit the change.
  const onPointerDown = (): void => {
    props.onPointerDown?.();
    removePointerReleaseListeners();
    const onPointerRelease = (): void => {
      removePointerReleaseListeners();
      props.onChangeEnded(colorRef.current);
    };
    onPointerReleaseRef.current = onPointerRelease;
    window.addEventListener('pointerup', onPointerRelease);
    window.addEventListener('pointercancel', onPointerRelease);
  };

  /**
   * A change with no pointer interaction in flight never gets a pointerup, so it must commit
   * itself. Two paths land here: the compatibility mousedown a browser replays after a touch TAP
   * (react-colorful treats it as a fresh interaction and re-emits a live change after our
   * pointerup already committed — uncommitted, it left item controls stuck hidden on mobile),
   * and keyboard arrow-key adjustments (which produce no pointer events at all).
   */
  const emitChange = (newColor: RGBA): void => {
    colorRef.current = newColor;
    props.onChange(newColor);
    if (!onPointerReleaseRef.current) {
      props.onChangeEnded(newColor);
    }
  };

  useEffect(() => {
    return (): void => {
      removePointerReleaseListeners();
    };
  }, []);

  return (
    <div className={`${className} saturation`}>
      {isAlphaDisabled ? (
        <HexColorPicker
          color={color as string}
          onChange={(value: string): void => {
            emitChange(hexToRgba(value));
          }}
          onPointerDown={onPointerDown}
        />
      ) : (
        <RgbaColorPicker
          color={rgbaToRgbacolor(color as RGBA)}
          onChange={(value: Rgbacolor): void => {
            emitChange(rgbacolorToRgba(value));
          }}
          onPointerDown={onPointerDown}
        />
      )}
    </div>
  );
}
